import type { ChatMessageSource } from '../../../types/chatMessage';

export function isKnowledgeBackedSource(
  sourceType: string | undefined,
): sourceType is 'KDB_DOCUMENT' | 'CRAWL' {
  return sourceType === 'KDB_DOCUMENT' || sourceType === 'CRAWL';
}

export function isInternetSource(
  sourceType: string | undefined,
): sourceType is 'INTERNET' {
  return sourceType === 'INTERNET';
}

export function isTrainingExampleSource(
  sourceType: string | undefined,
): sourceType is 'TRAINING_EXAMPLE' {
  return sourceType === 'TRAINING_EXAMPLE';
}

export function isTrainingExampleDeleted(source: ChatMessageSource): boolean {
  return source.refStatus === 'TRAINING_EXAMPLE_DELETED';
}

export function sourceFileTitle(source: ChatMessageSource): string {
  return source.documentName?.trim() || source.sourceTitle?.trim() || '';
}

export function internetSourceTitle(source: ChatMessageSource): string {
  return source.sourceTitle?.trim() ?? '';
}

export function trainingExampleTitle(source: ChatMessageSource): string {
  return source.sourceTitle?.trim() ?? '';
}

export function sourceDisplayName(source: ChatMessageSource): string {
  return (
    sourceFileTitle(source) ||
    trainingExampleTitle(source) ||
    source.knowledgeBaseName?.trim() ||
    source.sourceUrl?.trim() ||
    source.documentId?.trim() ||
    source.trainingExampleId?.trim() ||
    '—'
  );
}

export function resolveSourceKnowledgeBaseId(
  source: ChatMessageSource,
  fallbackKnowledgeBaseId: string | undefined,
  knowledgeBases: ReadonlyArray<{ id: string; name: string }> | undefined,
): string {
  const fromSource = source.knowledgeBaseId?.trim() ?? '';
  if (fromSource.length > 0) {
    return fromSource;
  }

  const name = source.knowledgeBaseName?.trim().toLowerCase() ?? '';
  if (name.length > 0 && knowledgeBases) {
    const match = knowledgeBases.find(
      (knowledgeBase) => knowledgeBase.name.trim().toLowerCase() === name,
    );
    if (match) {
      return match.id;
    }
  }

  return fallbackKnowledgeBaseId?.trim() ?? '';
}

export function isSourceKnowledgeBaseInaccessible(
  source: ChatMessageSource,
  accessibleKnowledgeBaseIds: Set<string> | null,
): boolean {
  if (
    accessibleKnowledgeBaseIds == null ||
    !isKnowledgeBackedSource(source.sourceType)
  ) {
    return false;
  }

  const knowledgeBaseId = source.knowledgeBaseId?.trim() ?? '';
  if (knowledgeBaseId.length === 0) {
    return false;
  }

  return !accessibleKnowledgeBaseIds.has(knowledgeBaseId);
}
